<!--
  NumberInput.vue

  Form input for number-kind fields. Integer / percent inherit via the FieldType
  chain — their `parse` overrides flow through `useField` automatically. Currency
  has its own CurrencyInput (a native number input can't hold a "$50,000" string).
-->
<template>
  <input
    class="input"
    :class="`is-${size}`"
    :type="inputType"
    :inputmode="inputType === 'text' ? 'decimal' : undefined"
    :value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autofocus="autofocus"
    @input="handleInput"
    @wheel="($event.target as HTMLInputElement).blur()"
  />
</template>

<script setup lang="ts">
import { computed } from "vue"
import type { Field } from "@auger/core/fields"
import { useField } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    field: Field
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    autofocus?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    autofocus: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput, displayed } = useField(props.field, emit)

const formattedValue = computed(() => displayed(props.modelValue) as string | number | null)

// Percent formats to a non-numeric string ("40%"), which a native number input
// silently refuses to display. Render it as text (its `parse` strips the glyph
// back to a number on input); plain number / integer keep the numeric input with
// its stepper + validation.
const inputType = computed(() => (props.field.type === "percent" ? "text" : "number"))
</script>
